<script setup>
import ComparisonTip from 'vue-elements/comparison-elements/ComparisonTip.vue';
import Icon from 'vue-elements/icon/Icon.vue';
import {
  computed, onMounted, ref, watch,
} from 'vue';
import { useRouter } from 'vue-router';
import TransitionProductCards from 'vue-elements/transitions/TransitionProductCards.vue';
import { isEqual, reduce } from 'lodash';
import PostalBox from 'vue-elements/comparison-inputs/PostalBox.vue';
import FilterBar from '../components/FilterBar.vue';
import Loader from '../components/Loader.vue';

import SaveEnergyComparison from '../components/SaveEnergyComparison.vue';

import ButtonIconLeft from '../../../../abstracts/elements/button/icon-left/button-icon-left.vue';

import { useEnergyComparisonStore } from '../stores/energyComparison';
import { filtersToUrlParams } from '../lib/filters';
import { useFrankStore } from '../../frank/store/frankStore';
import FrankModal from '../../frank/FrankModal.vue';
import { logAnalyticsEvent } from '../../../../assets/js/helpers/bananas.js';
import LinkIconLeft from '../../../../abstracts/elements/link/icon-left/link-icon-left.vue';
import SelectInput from '../../../vue-elements/input/SelectInput.vue';

import { sortModes } from '../lib/filterOptions.js';
import EnergyProduct from '../components/productCards/EnergyProduct.vue';
import ModalCenter from '../../../../abstracts/elements/modal/center/modalCenter.vue';
import ModalHook from '../../../../abstracts/elements/modal/_shared/hook/modalHook.vue';
import FormattedEuro from '../../../vue-elements/product-card/FormattedEuro.vue';
import ProductOverviewSeperator from '../components/ProductOverviewSeperator.vue';

/** Frank implementation */

const comparisonStore = useEnergyComparisonStore();
const frankStore = useFrankStore();
const router = useRouter();

const showMobileFilters = ref(false);
const hideMobileFilterButton = ref(false);

onMounted(() => {
  frankStore.setFrankComparer('energy');
});

const setFrankProduct = (product) => {
  frankStore.setFrankProduct(product);
};

// Props:
const props = defineProps({
  urlPostcode: {
    type: String,
    default: null,
  },
  urlNumber: {
    type: String,
    default: null,
  },
  urlNumberExtension: {
    type: String,
    default: null,
  },
  urlFilters: {
    type: Object,
    default: null,
  },
  orderFlowSignup: {
    type: Boolean,
    default: false,
  },
});

// Emits:
const emit = defineEmits(['updateAddressBar', 'update:orderFlowSignup']);

// Load products on mount:
onMounted(async () => {
  if (
    props.urlPostcode !== comparisonStore.postalCode
    || props.urlNumber !== comparisonStore.houseNumber
    || props.urlNumberExtension !== comparisonStore.houseNumberAddition) {
    comparisonStore.loadPostalLocation(props.urlPostcode, props.urlNumber, props.urlNumberExtension)
      .catch(() => {
      });
  }
  if (props.urlFilters.contractTypes && !props.urlFilters.contractTypes.includes('dynamic')) {
    comparisonStore.tariffType = 'fixed';
  } else {
    comparisonStore.tariffType = 'dynamic';
  }
  // Load filters from URL:
  if (props.urlFilters) {
    comparisonStore.filters = props.urlFilters;
  }

  await comparisonStore.load();
});

// Watch filters in store, reload products when changed:
watch(() => comparisonStore.filters, async () => {
  await comparisonStore.load();
}, { deep: true });

watch(() => comparisonStore.compareLocation, async (newVal) => {
  if (newVal && newVal.display) {
    emit('updateAddressBar', 'Energie vergelijken');
  } else if (!comparisonStore.loading) {
    emit('updateAddressBar', 'Vul je adres in om te vergelijken');
  }
}, {
  deep: true,
  immediate: true,
});

const extremePowerConsumption = computed(() => {
  if (comparisonStore.loading) {
    return false;
  }

  if (parseInt(comparisonStore.filters.gas, 10) > 25000) {
    return true;
  }

  let powerConsumption = parseInt(comparisonStore.filters.electricity, 10);

  if (comparisonStore.filters.electricityGenerated) {
    powerConsumption += parseInt(comparisonStore.filters.electricityOffPeak, 10);
  }
  if (comparisonStore.filters.electricityGenerated) {
    powerConsumption -= parseInt(comparisonStore.filters.electricityGeneration, 10);
  }

  if (Number.isNaN(powerConsumption)) {
    return false;
  }

  return powerConsumption > 50000;
});

const filterQuery = computed(() => {
  const queryObject = {
    postcode: comparisonStore.postalCode,
    nr: comparisonStore.houseNumber,
    ...filtersToUrlParams(comparisonStore.filters),
  };

  if (comparisonStore.houseNumberAddition) {
    queryObject.extension = comparisonStore.houseNumberAddition;
  }

  return queryObject;
});

const productCountButton = computed(() => (comparisonStore.totalProducts !== 1
  ? `Toon ${comparisonStore.totalProducts} aanbiedingen`
  : 'Toon 1 aanbieding'));

// Actions:
const changeAddress = () => {
  router.push({
    name: 'wizard',
    query: {
      ...filterQuery.value,
      step: 'PostalStep',
    },
  });
};

// Watch filter query, update URL when filters change:
watch(
  filterQuery,
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      // Replace URL:
      router.replace({
        name: 'compare',
        query: filterQuery.value,
      });
    }
  },
  { deep: true },
);

// json version of filter object for events
const jsonFilters = computed(() => JSON.stringify(comparisonStore.filters));
watch(jsonFilters, async (newFilters, oldFilters) => {
  const jsonOldFilters = JSON.parse(oldFilters);
  const jsonNewFilters = JSON.parse(newFilters);

  await comparisonStore.load();

  const changedFilters = reduce(
    jsonOldFilters,
    (result, value, key) => (isEqual(value, jsonNewFilters[key]) ? result : result.concat(key)),
    [],
  );

  changedFilters.forEach((filterName) => {
    let filterValue = null;

    if (comparisonStore.filters[filterName]) {
      filterValue = comparisonStore.filters[filterName];
    }

    logAnalyticsEvent('compare-energy.filter', {
      filter_name: filterName,
      filter_value: filterValue,
    });
  });
});

const openSaveComparisonModal = () => {
  document.querySelector('input#save-comparison')
    .click();
};

const productCardType = (product) => {
  return EnergyProduct;
}

const tipModal = ref(null);

const editUsage = () => {
  tipModal.value?.close();
  showMobileFilters.value = true;
}


</script>

<template>
  <div class="inner compare-overview-container comparison-overview-view">
    <div class="inner compare-overview-container comparison-overview-view">
      <div class="energy-flow-container">

        <div class="comparison-postal" />
        <transition name="fade" mode="out-in">
          <div v-if="!comparisonStore.loading && !comparisonStore.locationFound || comparisonStore.locationLoading"
            class="postal-box-mobile">
            <div class="panel">
              <postal-box :location="comparisonStore.compareLocation" :loading="comparisonStore.loading"
                @postal="comparisonStore.doPostal($event)" />
            </div>
          </div>
        </transition>
        <div class="comparison-body" :class="{
          'is-loading': comparisonStore.loading,
          'no-user-address': !comparisonStore.locationReady || extremePowerConsumption,
        }">
          <filter-bar :loading="comparisonStore.loading" :address-ready="comparisonStore.locationReady"
            :show="showMobileFilters" :saved-comparison="comparisonStore.savedComparison" :signed-in="false"
            @changeAddress="changeAddress()" @filterButton="hideMobileFilterButton = $event"
            @hideFilters="showMobileFilters = false" />

          <div v-if="!hideMobileFilterButton" class="floating-panel" :class="{ expand: showMobileFilters }"
            @click="showMobileFilters = false">
            <div class="btn primary" :class="{ 'is-loading-state': comparisonStore.loading }" role="button">
              <loader v-if="comparisonStore.loading" />
              <template v-else>
                {{ productCountButton }}
                <icon icon="chevron-right" />
              </template>
            </div>
          </div>
          <div>
            <ModalCenter
              v-if="!comparisonStore.loading && comparisonStore.products.length > 0 && (comparisonStore.modelContractCurrentProvider && comparisonStore.savingsPerYear)"
              class="savings-tip-modal" ref="tipModal" name="tip-modal" @close="showTipModal = false">
              <template #content>
                <div class="">
                  <h4>Waarom besparen door over te stappen?</h4>

                  <p>Als je vaste energiecontract afloopt, of als je al een tijdje niet bent overgestapt, krijg je
                    automatisch een variabel contract. <span class="light-bold">Op basis van jouw situatie hebben wij
                      uitgerekend dat jij
                      kunt besparen door over te stappen van leverancier.</span></p>

                  <div class="price-preview">
                    <div class="price-element" v-if="comparisonStore.modelContractCurrentProvider">
                      <span class="price-label">{{ comparisonStore.currentProvider }} variabel contract</span>

                      <FormattedEuro :price="comparisonStore.modelContractPerYear" appendage=" / jaar" />
                    </div>
                    <div class="price-element" v-else>
                      <span class="price-label">Gemiddelde variabele contract</span>

                      <FormattedEuro :price="comparisonStore.modelContractPerYear" appendage=" / jaar" />
                    </div>
                    <div class="price-element">
                      <span class="price-label">Goedkoopste contract</span>
                      <FormattedEuro :price="comparisonStore.products[0].year_price" appendage=" / jaar" />
                    </div>
                    <hr class="divider">
                    <div class="price-element bold">
                      <span class="price-label">Jouw besparing</span>
                      <FormattedEuro :price="comparisonStore.savingsPerYear" />
                    </div>
                  </div>

                  <template v-if="comparisonStore.currentProvider != 'moving' && comparisonStore.currentProvider">
                    <p><strong>Verlengaanbieding ontvangen van {{ comparisonStore.currentProvider }}?</strong></p>


                    <p class="mobile-content"><button @click="editUsage" class="text-link clean-button"> Vul hetzelfde
                        verbruik in</button> dat
                      {{
                        comparisonStore.currentProvider }}
                      voor je verlengaanbod heeft gebruikt en vergelijk de prijs per jaar. Onze ervaring is dat
                      overstappen naar een nieuwe leverancier vrijwel altijd
                      voordeliger is. Maar op deze manier kun je voor jouw situatie checken wat het beste is.
                    </p>

                    <p class="desktop-content">Vul hetzelfde verbruik in dat
                      {{
                        comparisonStore.currentProvider }}
                      voor je verlengaanbod heeft gebruikt en vergelijk de prijs per jaar. Onze ervaring is dat
                      overstappen naar een nieuwe leverancier vrijwel altijd
                      voordeliger is. Maar op deze manier kun je voor jouw situatie checken wat het beste is.
                    </p>
                  </template>

                  <ModalHook class="close-button" name="tip-modal" @open="showTipModal = true">
                    <template #content>
                      <div class="btn primary">Verder met vergelijken</div>
                    </template>
                  </ModalHook>


                  <!-- <p><span class="link-element"> Vul hetzelfde verbruik in</span> dat {{ comparisonStore.currentProvider
                    }}
                    voor je verlengaanbod heeft gebruikt en vergelijk de prijs per jaar.</p>
                  <p>Onze ervaring is dat overstappen naar een nieuwe leverancier vrijwel altijd voordeliger is. Maar op
                    deze manier kun je voor jouw situatie checken wat het beste is.</p> -->

                </div>
              </template>
            </ModalCenter>
            <!-- Tips about usage and product types -->
            <div class="tip-container">
              <TransitionGroup name="slide-in-out" appear>
                <comparison-tip v-if="!comparisonStore.locationReady" class="postal-needed warning"
                  :style="{ 'transition-delay': '.3s' }" icon="x-fa-arrow-left" :show-close="false">
                  <template #message>
                    Vergelijk met de juiste prijzen door je
                    <strong>postcode</strong> en <strong>huisnummer</strong> in te
                    vullen.
                  </template>
                </comparison-tip>

                <comparison-tip v-else-if="extremePowerConsumption" class="warning extreme-power-consumption"
                  :showClose="false" icon="x-fa-arrow-left">
                  <template #message>
                    Je hebt een erg hoog verbruik ingevuld, weet je zeker dat dit
                    klopt? Voor dit verbruik kunnen we je geen aanbiedingen laten
                    zien.
                    <button @click="editUsage" class="text-link clean-button mobile-content">Verbruik aanpassen</button>
                    <br>
                    <br>
                    Als je verbruik klopt heb je een (groot)zakelijk
                    energiecontract nodig. Hiervoor kun je het beste gebruik maken
                    van onze
                    <a href="/zakelijk/energie-vergelijken" target="_blank">zakelijke energievergelijker</a>.
                  </template>
                </comparison-tip>

                <template v-else-if="!comparisonStore.error && comparisonStore.totalProducts > 0">

                  <comparison-tip
                    v-if="comparisonStore.filters.electricityGenerated && comparisonStore.electricityGeneration > 0"
                    icon="x-fa-lightbulb" :show-close="true">
                    <template #message>
                      Dit zijn de goedkoopste deals voor je huis met zonnepanelen!
                      We houden rekening met alle <strong>terugleverkosten.</strong>
                    </template>
                  </comparison-tip>

                  <comparison-tip v-else-if="comparisonStore.filters.contractTypes.includes('dynamic')"
                    icon="x-fa-check" :show-close="true">
                    <template #message>
                      <strong>Je vergelijkt nu dynamische contracten</strong>
                      <br>
                      Wij rekenen voor iedereen met dezelfde verwachte beursprijs. Hierdoor vergelijk je op een eerlijke
                      manier dynamische energiecontracten.
                    </template>
                  </comparison-tip>

                  <comparison-tip v-else-if="comparisonStore.filters.currentProvider === 'moving'" icon="x-fa-check"
                    :show-close="true">
                    <template #message>
                      <span class="savings-title">Je kunt besparen op je energierekening!</span><br>
                      Verhuisgarantie: Je zit nooit zonder gas of stroom, zelfs al gaat je contract later in.
                    </template>
                  </comparison-tip>
                  <comparison-tip v-else-if="!['Frank Energie', 'Nextenergy', 'ANWB Energie', 'Tibber', 'Noord Energie'].includes(comparisonStore.currentProvider)
                    && comparisonStore.modelContractPerYear !== null
                    && !(comparisonStore.filters.contractTypes.length === 1
                    && comparisonStore.filters.contractTypes[0] === '1')" icon="" :show-close="true" class="no-icon">
                    <template #message>
                      <strong>Jij kunt besparen!</strong> <br>
                      <span v-if="comparisonStore.modelContractCurrentProvider">
                        Door over te stappen vanaf {{ comparisonStore.currentProvider }} bespaar je
                        <span class="light-bold">{{ toCurrencyShortEur(comparisonStore.savingsPerYear) }} per
                          jaar.</span>
                      </span>

                      <ModalHook name="tip-modal" @open="showTipModal = true">
                        <template #content>
                          <span class="link-element">
                            <Icon icon="x-fa-calculator"></Icon> Hoe berekenen wij dit?
                          </span>
                        </template>
                      </ModalHook>
                    </template>
                  </comparison-tip>


                  <comparison-tip v-else icon="x-fa-check" :show-close="true">
                    <template #message>
                      Dit zijn de beste deals voor jouw situatie!
                    </template>
                  </comparison-tip>

                  <comparison-tip v-if="comparisonStore.filters.sort === 'monthly_installment'" icon="x-fa-info-circle"
                    :show-close="true">
                    <template #message>
                      Je ziet nu het maandelijks termijnbedrag exclusief korting. Een laag termijbedrag betekent niet
                      altijd dat het contract goedkoper is. Vergelijk altijd de jaarprijs.</template>
                  </comparison-tip>
                </template>

              </TransitionGroup>
            </div>

            <!-- Open Filter Button -->
            <div class="comparison-sorters">
              <LinkIconLeft class="show-filter-link" type="ghost" label="Filters" icon="slider-light"
                @click.native="showMobileFilters = true" />

              <SelectInput class="sorter-select" id="sort-modes" v-model="comparisonStore.filters.sort"
                :options="sortModes" type="border-bottom" />
            </div>
            <div class="products"
              :class="{ 'initial-load': !comparisonStore.loading, 'inactive': !comparisonStore.addressReady }">

              <TransitionProductCards>
                <div v-if="!comparisonStore.loading && comparisonStore.error" class="product-error no-products error">
                  <icon icon="x-fa-exclamation-triangle" />
                  <div class="error-message">
                    <div>Door een fout kunnen we geen informatie laten zien.</div>
                  </div>
                  <div>
                    {{ comparisonStore.errorMessage }}
                  </div>

                  <div class="error-button">
                    <button-icon-left type="cta" label="Probeer opnieuw" icon="x-fa-redo"
                      @click.native="comparisonStore.load()" />
                  </div>
                </div>

                <div
                  v-else-if="comparisonStore.products && comparisonStore.products.length === 0 && !comparisonStore.loading"
                  class="product-error no-products">
                  <icon icon="x-fa-exclamation-circle" />


                  <template
                    v-if="comparisonStore.filters.contractTypes.includes('dynamic') && !comparisonStore.filters.doubleMeter">
                    <span class="error-message">Slimme meter verplicht bij een dynamisch contract </span>
                    <p>Je hebt aangegeven dat je geen slimme meter hebt. Helaas is het niet mogelijk om een dynamisch
                      contract af te sluiten zonder slimme meter.</p>
                    <button class="btn primary" @click.native="comparisonStore.resetFilters()">
                      Vergelijk vaste contracten
                    </button>
                  </template>
                  <template v-else>
                    <span class="error-message" style="text-align: center">
                      Er zijn geen aanbiedingen gevonden.
                      We hebben geen resultaten gevonden met deze filters.
                      Zet je filters terug om alle aanbiedingen te zien.
                    </span>
                    <div class="error-button">
                      <button-icon-left type="cta" label="Filters terugzetten" icon="x-fa-redo"
                        @click.native="comparisonStore.resetFilters()" />
                    </div>
                  </template>

                </div>
                <template v-for="(product, index) in comparisonStore.products">
                  <EnergyProduct v-if="(!comparisonStore.error && comparisonStore.paginating) || !comparisonStore.error"
                    :key="`product-${product.combination_id}`" :product="product" />
                  <ProductOverviewSeperator v-if="index === 2" :key="'custom-component'" />
                  <ProductOverviewSeperator v-if="index === 5" :key="'custom-component-2'" :mobileOnly="true"
                    :usps="['14 dagen bedenktijd']" />
                  <ProductOverviewSeperator v-if="index === 8" :key="'custom-component-3'" :mobileOnly="true"
                    :usps="['Gratis overstapservice']" />
                </template>

              </TransitionProductCards>

              <button v-if="comparisonStore.hasMoreProducts && !comparisonStore.loading && !comparisonStore.error"
                class="button more" :class="{ 'is-loading-state': comparisonStore.paginating }" @click="
                  $event.target.blur();
                comparisonStore.loadMore();
                ">
                <template v-if="comparisonStore.paginating">
                  <loader />
                </template>
                <template v-else-if="!comparisonStore.loading">
                  <icon icon="x-fa-plus" size="m" />

                  <template v-if="comparisonStore.moreProducts === 1">
                    {{ comparisonStore.moreProducts }} aanbieding
                  </template>
                  <template v-else>
                    {{ comparisonStore.moreProducts }} aanbiedingen
                  </template>
                </template>

                <template v-else>
                  <loader />
                </template>
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
    <save-energy-comparison :location="comparisonStore.compareLocation" :products="comparisonStore.products"
      :filters="comparisonStore.filters" @savedComparison="comparisonStore.setSavedComparison($event)" />
    <FrankModal @save="openSaveComparisonModal" />
  </div>
</template>
